// src/services/resumes.js
//
// The list of a person's resume versions, read in one place.
//
// Two screens read it — the jobs page, to pick which resume to apply with, and
// the profile's version switcher — and only one of them writes it. That is
// exactly the arrangement a cache gets wrong: the writer drops what it knows
// about, and the other reader keeps showing a version that was renamed or
// deleted minutes ago.
//
// So the key lives here with the read, and so does the one function every
// writer has to call beside it. Neither screen knows the string.
import { auth } from '../components/FirebaseConfig';
import { API_BASE, fetchWithTimeout } from '../apiSession';
import { cached, invalidate } from './queryCache';

const KEY = 'resumes';

// Long, because a person's own edits invalidate this immediately. Only another
// tab's changes have to wait it out.
const TTL = 5 * 60_000;

/**
 * Every resume version this person has.
 *
 * THROWS on failure, and the message is meant to be shown. This used to fail
 * silently, which left an empty dropdown labelled "My Resume" and no hint that
 * anything was wrong — the failure stayed invisible until some later action
 * happened to surface it. A caller that genuinely does not care can catch.
 *
 * @param {{force?: boolean}} [opts] `force` skips the cache. Used by the
 *   resume gate: it may only BLOCK somebody on a fresh answer, because a
 *   five-minute-old "no resume" would keep a person out of the site for five
 *   minutes after they built one.
 * @returns {Promise<Array>}
 */
export async function fetchResumes({ force = false } = {}) {
  return cached(KEY, async () => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error('Sign in to see your resumes.');
    const res = await fetchWithTimeout(`${API_BASE}/api/resumes`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      // A 404 here means the endpoint is not registered — almost always a
      // server that has not restarted since a deploy. Saying so directly beats
      // leaving someone to infer it from a status code in the console.
      throw new Error(res.status === 404
        ? "Resume versions aren't available yet. The server needs a restart to register this endpoint."
        : `Request failed (HTTP ${res.status}).`);
    }
    const json = await res.json();
    return Array.isArray(json?.resumes) ? json.resumes : [];
  }, { ttl: TTL, force });
}

/**
 * Called after creating, renaming, deleting or activating a resume.
 *
 * Also drops the feed and any search results. Both are SCORED against the
 * active resume, so changing which one is active makes every number they are
 * displaying a score for a different document.
 */
export function invalidateResumes() {
  invalidate(KEY);
  invalidate('feed:');
  invalidate('search');

  // Two components listen for this and nothing had ever dispatched it — the
  // resume switcher's own refresh, and the gate that decides whether the rest
  // of the site is usable. Fired here because this is the one function every
  // writer already has to call.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('resumes-changed'));
  }
}
